/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing';
import { ModalScrollScreen } from '../../shared/layout/ModalScrollScreen'
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { ScreenHeroCard } from '../../shared/surface-cards/ScreenHeroCard';
import { settingsStyles } from './settingsScreen.styles';
import { ticketColors } from '../../shared/theme/ticketColors';

// ponytail: glosario estático — PTR solo para consistencia de gesto en shell.

/** Misma voz en PC y celular. Caption corta; si hace falta un párrafo, sobra. */
const GLOSSARY_LEAD =
  'Palabras de stock, caja e informes. Si hace falta más, escribinos en Soporte.';

const GLOSSARY_SECTIONS = [
  {
    title: 'Stock e inventario',
    icon: 'cube-outline' as const,
    terms: [
      {
        name: 'Ajuste',
        text: 'Corrige unidades (entrada, salida o merma) sin venta. Afecta el stock al sincronizarse.',
      },
      {
        name: 'Transferencia',
        text: 'Movés mercadería de una sucursal a otra. Origen descuenta; destino suma al recibir.',
      },
      {
        name: 'Conteo físico',
        text: 'Contás lo que hay para alinear el stock con lo real. Suele dejar un ajuste por diferencia.',
      },
      {
        name: 'Stock mínimo',
        text: 'Debajo de este nivel conviene reponer. En Informes ves los productos bajo mínimo.',
      },
      {
        name: 'Entrada',
        text: 'Suma stock por compra, devolución o carga a mano.',
      },
    ],
  },
  {
    title: 'Informes y caja',
    icon: 'receipt-outline' as const,
    terms: [
      {
        name: 'Caja (turno)',
        text: 'Período con la plata y los movimientos de un encargado. Se abre y se cierra para cuadrar.',
      },
      {
        name: 'Sincronización',
        text: 'Sube y baja ventas, stock y caja con el servidor. El ícono de nube muestra pendientes.',
      },
      {
        name: 'Informes',
        text: 'Resumen de ventas, movimientos o caja, según tus permisos.',
      },
    ],
  },
];

const Term = ({ name, text, mobile }: { name: string; text: string; mobile?: boolean }) =>
  mobile ? (
    <View style={mobileStyles.term}>
      <Text style={mobileStyles.termName}>{name}</Text>
      <Text style={mobileStyles.termText}>{text}</Text>
    </View>
  ) : (
    <View style={localStyles.term}>
      <Text style={settingsStyles.captionStrong}>{name}</Text>
      <Text style={settingsStyles.caption}>{text}</Text>
    </View>
  );

const Section = ({
  title,
  icon,
  children,
  mobile,
}: {
  title: string;
  icon: React.ComponentProps<typeof Ionicons>['name'];
  children: React.ReactNode;
  mobile?: boolean;
}) =>
  mobile ? (
    <View style={mobileStyles.group}>
      <View style={mobileStyles.groupLabelRow}>
        <Ionicons name={icon} size={13} color={ticketColors.ink3} />
        <Text style={mobileStyles.groupLabel}>{title}</Text>
      </View>
      <View style={mobileStyles.groupCard}>{children}</View>
    </View>
  ) : (
    <View style={[settingsStyles.card, localStyles.section]}>
      <Text style={settingsStyles.cardTitle}>{title}</Text>
      {children}
    </View>
  );

export function GlossaryScreen() {
  const { layoutWide, layoutWebShell } = useContentLayout('settingsWide');
  const mobile = !layoutWebShell;
  const [refreshing, setRefreshing] = useState(false);
  const onRefresh = useCallback(() => {
    // ponytail: contenido estático — spinner corto solo para no romper el gesto esperado
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 250);
  }, []);

  if (mobile) {
    return (
      <ModalScrollScreen
        variant="settingsWide"
        contentContainerStyle={mobileStyles.content}
        refreshing={refreshing}
        onRefresh={onRefresh}
      >
        <View style={mobileStyles.header}>
          <Text style={mobileStyles.headerLead}>{GLOSSARY_LEAD}</Text>
        </View>

        <View style={mobileStyles.groups}>
          {GLOSSARY_SECTIONS.map((section) => (
            <Section key={section.title} title={section.title} icon={section.icon} mobile>
              {section.terms.map((term) => (
                <Term key={term.name} mobile name={term.name} text={term.text} />
              ))}
            </Section>
          ))}
        </View>
      </ModalScrollScreen>
    );
  }

  return (
    <ModalScrollScreen
      variant="settingsWide"
      contentContainerStyle={localStyles.content}
      refreshing={refreshing}
      onRefresh={onRefresh}
    >
      <ScreenHeroCard>
        <Text style={settingsStyles.screenLead}>{GLOSSARY_LEAD}</Text>
      </ScreenHeroCard>
      <View style={layoutWide ? localStyles.sectionsWide : localStyles.sectionsStack}>
        {GLOSSARY_SECTIONS.map((section) => (
          <Section key={section.title} title={section.title} icon={section.icon}>
            {section.terms.map((term) => (
              <Term key={term.name} name={term.name} text={term.text} />
            ))}
          </Section>
        ))}
      </View>
    </ModalScrollScreen>
  );
}

const localStyles = StyleSheet.create({
  content: {
    paddingBottom: spacing.xxl,
    gap: spacing.md,
  },
  sectionsWide: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'flex-start',
  },
  sectionsStack: {
    gap: spacing.md,
  },
  section: {
    flex: 1,
    minWidth: 280,
  },
  term: {
    marginTop: spacing.md,
    gap: 4,
  },
});

const mobileStyles = StyleSheet.create({
  content: {
    paddingBottom: spacing.xxl,
    gap: spacing.md,
  },
  header: {
    paddingTop: spacing.md,
    gap: 4,
  },
  headerLead: {
    fontSize: 14,
    color: ticketColors.ink2,
    lineHeight: 20,
  },
  groups: {
    gap: spacing.md + 2,
  },
  group: {
    gap: 6,
  },
  groupLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingLeft: 4,
    paddingBottom: 2,
  },
  groupLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  groupCard: {
    backgroundColor: ticketColors.paper,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    overflow: 'hidden',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  term: {
    paddingVertical: 10,
    gap: 3,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  termName: {
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  termText: {
    fontSize: 13,
    color: ticketColors.ink2,
    lineHeight: 18,
  },
});
